import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { api } from "api";
import {
  APPLICATION_STATUS,
  ApplicationSchemaDto,
  GetClearApplicationDto,
  T_CustomStrategy,
  IntelligenceDto,
  CreditorSplitFormat,
  StrategyType,
  TeamRole,
  TeamMember,
  Meeting,
} from "dtos/application.dto";
import { LeadSchemaDto } from "dtos/lead.dto";
import { UserSchemaDto } from "dtos/user.dto";
import { T_Api_Error_Res, T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import { LoanSchemaDto } from "dtos/loan.dto";
import { LawSuitSchemaDto } from "dtos/lawsuit.dto";
import { BankSchemaDto } from "dtos/bank.dto";
import { FileSchemaDto } from "dtos/file.dto";
import { ReportDto, ReportSchemaDto } from "dtos/report.dto";
import { useAppDispatch } from "store";
import { loadSavedData, loanIntelligenceInit, T_LoanIntelligence } from "store/loanIntelligence";
import { AxiosError, AxiosResponse } from "axios";
import { FlowData } from "pages/Journey/Journey";
import { LedgerSchemaDto } from "dtos/ledger.dto";
import { useDebounce } from "hooks/use-debounce";
import { BackgroundLog } from "pages/BackgroundJobLogs/BackgroundJobLogs";
import { StrategyConfigV2 } from "dtos/strategy.dto";
import { AppCreditorMapSchemaDto } from "dtos/app-creditor-map.dto";
import { QueryRoot, Stripped, T_Populate } from "types/_utils.types";
import {
  T_CreateManualEntryRequest,
  T_LoanScopeParams,
  T_MergedTransaction,
  T_UpdateManualEntryRequest,
} from "dtos/loan-transaction.dto";

// type PopulatedApplication = T_Replace<
//   T_Replace<ApplicationSchemaDto, "lead", LeadSchemaDto>,
//   "affiliate",
//   UserSchemaDto
// >;

export enum CalculatorValidationCodes {
  WEEK_BUDGET_OVERFLOW = "E0202",
  WEEK_BUDGET_UNDERFLOW = "W0202",
  TOTAL_WEEKS_OVERFLOW = "E0203",
  CREDITOR_TOTAL_OVERFLOW = "E0201",
  CREDITOR_TOTAL_UNDERFLOW = "W0201",
  ZERO_SEGMENT_SPLIT = "E0205",
  SEGMENT_SPLIT_UNDERFLOW = "W0205",
  LAST_SEGMENT_ZERO_SPLIT = "W0206",
  SEGMENT_BUDGET_OVERFLOW = "W0207",
  SEGMENT_BUDGET_UNDERFLOW = "E0207",
  INVALID_SEGMENT_RANGE = "E0208",
  INVALID_SEGMENT_DISTRIBUTION = "E0209",
  ZERO_PAYMENT_SEGMENT = "E0210",
  OVERFLOW_TENURE = "E0211",
  UNDERFLOW_TENURE = "W0211",
  ZERO_CAPACITY_SPLIT = "E0212",
  LOW_CAPACITY_SPLIT = "W0212",
}

type PopulatedApplication = T_Populate<
  ApplicationSchemaDto,
  {
    lead: LeadSchemaDto;
    affiliate: UserSchemaDto;
    organisation: {
      meta: {
        logo: string;
        favicon: string;
        title: string;
        company_name: string;
      };
      _id: string;
      name: string;
      code: string;
    };
  }
>;

type T_BulkUpdateTransactionsBody = {
  accepted_ids?: string[];
  rejected_ids?: string[];
};

type T_BulkUpdateTransactionsParams = {
  appKey: string;
  loanId: string;
  body: T_BulkUpdateTransactionsBody;
};

export const applicationService = {
  useQueryApps: <Q extends QueryRoot<PopulatedApplication>>(
    payload: Q,
    params: {
      page: number;
      limit: number;
      archived?: boolean;
      filter: {
        last_step?: (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS][];
        createdAt?: {
          startDate?: string;
          endDate?: string;
        };
        updatedAt?: {
          startDate?: string;
          endDate?: string;
        };
        affiliate?: string[];
        organisation?: string[];
      };
      sort?: Partial<Record<keyof ApplicationSchemaDto, 1 | -1>>;
      search?: string;
    },
  ) => {
    return useQuery({
      queryKey: ["application-query", payload, params],
      queryFn: async () => {
        try {
          const { data } = await api.request<T_Api_Success_Res<T_Api_Paginated<Stripped<PopulatedApplication, Q>>>>({
            method: "QUERY",
            url: "/application",
            params,
            data: payload,
          });

          return data.result;
        } catch {
          const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<PopulatedApplication>>>(`/application`, {
            params,
          });

          return data.result;
        }
      },
    });
  },
  useGetPaginated: (params: {
    page: number;
    limit: number;
    archived?: boolean;
    filter: {
      last_step?: (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS][];
      createdAt?: {
        startDate?: string;
        endDate?: string;
      };
      updatedAt?: {
        startDate?: string;
        endDate?: string;
      };
      affiliate?: string[];
      organisation?: string[];
    };
    sort?: { [key in keyof Partial<ApplicationSchemaDto>]: 1 | -1 } | { _id: 1 | -1 };
    search?: string;
  }) => {
    return useQuery({
      queryKey: ["application", params],
      queryFn: async () => {
        const res = await api.get(`/application`, { params });
        return res.data as T_Api_Success_Res<T_Api_Paginated<PopulatedApplication>>;
      },
    });
  },
  useGetPaginatedForOrg: (params: {
    page: number;
    limit: number;

    search?: string;
  }) => {
    const searchString = useDebounce(params.search, 500);
    const { search, ...rest } = params;
    return useQuery({
      queryKey: ["application", { ...rest, search: searchString }],
      queryFn: async () => {
        const res = await api.get(`/application`, { params: { ...rest, search: searchString } });
        return res.data as T_Api_Success_Res<T_Api_Paginated<PopulatedApplication>>;
      },
    });
  },
  useGetByAppKey: (appKey: string | undefined, page?: "loanIntelligence") => {
    const dispatch = useAppDispatch();
    return useQuery({
      queryKey: ["application", appKey, page],
      queryFn: async () => {
        const res = await api.get(`/application/${appKey}`);
        const data = res.data as T_Api_Success_Res<{
          data: {
            application: T_Populate<
              GetClearApplicationDto,
              {
                loans: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[];
                banks: T_Populate<BankSchemaDto, { attachments: FileSchemaDto[] }>[];
                lawsuits: T_Populate<LawSuitSchemaDto, { attachments: FileSchemaDto[] }>[];
                agreements: FileSchemaDto[];
                statement_files: FileSchemaDto[];
              }
            >;
          };
          application: ApplicationSchemaDto;
        }>;
        if (page === "loanIntelligence") {
          // console.log("dispatch ran");
          const getclearApp = data?.result?.data?.application;
          const adminApp = data?.result?.application;
          if (data.result?.application?.loan_intelligence?.budget) {
            dispatch(
              loadSavedData({
                ...data.result.application?.loan_intelligence,
                app_key: data.result.application?.app_key,
              }),
            );
          } else {
            dispatch(
              loanIntelligenceInit({
                app_key: adminApp?.app_key,
                loans: getclearApp?.loans,
                commissionPercentage: (getclearApp?.strategy?.interest_rate ?? 0.22) * 100,
                totalWeeklyBudget: getclearApp?.strategy?.weekly_payments ?? 0,
                negotiationBudget: adminApp?.payment_table?.[0]?.repeat_rows?.[0]?.row?.creditor ?? 0,
                monthlyRevenue: getclearApp.summary.monthly_revenue ?? 0,
                calculationMode:
                  getclearApp.strategy_type && getclearApp.strategy_type === StrategyType.PERCENTAGE
                    ? "percentage"
                    : "number",
              }),
            );
          }
        }
        return data?.result;
      },
      enabled: !!appKey,
    });
  },
  useGetByApplicationId: (application_id: string | undefined, page?: "loanIntelligence") => {
    return useQuery({
      queryKey: ["application", application_id, page],
      queryFn: async () => {
        const res = await api.get(`/application/id/${application_id}`);
        const data = res.data as T_Api_Success_Res<{
          data: {
            application: T_Populate<
              GetClearApplicationDto,
              {
                loans: T_Populate<LoanSchemaDto, { attachments: FileSchemaDto[] }>[];
                banks: T_Populate<BankSchemaDto, { attachments: FileSchemaDto[] }>[];
                lawsuits: T_Populate<LawSuitSchemaDto, { attachments: FileSchemaDto[] }>[];
                agreements: FileSchemaDto[];
                statement_files: FileSchemaDto[];
              }
            >;
          };
          application: ApplicationSchemaDto;
        }>;

        return data?.result;
      },
      enabled: !!application_id,
    });
  },
  useGetLoansMapping: (appKey: string | undefined) => {
    return useQuery({
      queryKey: ["loans-mapping", appKey],
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<AppCreditorMapSchemaDto[]>>(`/application/loans/${appKey}`);
        return data.result;
      },
      enabled: Boolean(appKey?.length),
    });
  },
  /** @deprecated */
  // useUpdateLoanStatus: () => {
  //   return useMutation({
  //     mutationFn: async ({
  //       appKey,
  //       loanId,
  //       status,
  //     }: {
  //       appKey: string | undefined;
  //       loanId: T_MongoId | undefined;
  //       status: LoanStatus;
  //     }) => {
  //       if (!appKey || !loanId) throw new Error("AppKey and LoanId must be valid");
  //       const { data } = await api.patch(`/application/loan/${loanId}?appKey=${appKey}`, { status });
  //       return data;
  //     },
  //   });
  // },
  useGenerateReport: () => {
    return useMutation({
      mutationKey: ["application", "generate"],
      mutationFn: async (body: { app_key: string }) => {
        const res = await api.post(`/application/audit`, body);
        return res.data as T_Api_Success_Res<Record<string, never>>; // Note: Record<string, never> means empty object
      },
    });
  },
  useGetReport: (appKey: string | undefined, reportId: string | undefined) => {
    return useQuery({
      queryKey: ["application/report", appKey, reportId],
      queryFn: async () => {
        const res = await api.get(`/application/report/${appKey}/${reportId}`);
        const data = res.data as T_Api_Success_Res<ReportSchemaDto>;
        return data?.result;
      },
      enabled: !!appKey,
    });
  },
  useGetPaymentTable: (appId: string | undefined) => {
    return useQuery({
      queryKey: ["application/payment-table", appId],
      queryFn: async () => {
        const res = await api.get(`/application/payment-table/${appId}`);
        const data = res.data as T_Api_Success_Res<{ payment_table: LedgerSchemaDto[] }>;
        return data?.result;
      },
      enabled: !!appId,
    });
  },
  useSaveLoanIntelligence: (cycle: "weekly" | "monthly") => {
    return useMutation({
      mutationKey: ["save/intelligence"],
      mutationFn: async (body: T_LoanIntelligence) => {
        // const convertToMonthly = (loans: T_LoanIntelligence["loans"]["current"]) => {
        //   return Object.fromEntries(
        //     Object.entries(loans).map(([creditor, loan]) => {
        //       return [
        //         creditor,
        //         {
        //           ...loan,
        //           startWeek: loan.startWeek ? loan.startWeek * 4 : undefined,
        //         },
        //       ];
        //     }),
        //   );
        // };

        // const payload: T_LoanIntelligence = {
        //   ...body,
        //   loans: {
        //     ...body.loans,
        //     current: cycle === "monthly" ? convertToMonthly(body.loans.current) : body.loans.current,
        //   },
        // };

        return await api.post(`/application/add/creditor-intel`, body);
      },
    });
  },

  useUpdateLedger: () => {
    return useMutation({
      mutationKey: ["update/ledger"],
      mutationFn: async (body: { new_program_fee: number; new_weeks: number; application_id: string }) => {
        return api.put(`/ledger/update/program-fee`, body);
      },
    });
  },
  useUpdateLedgerNotes: () => {
    return useMutation({
      mutationKey: ["update/ledger/notes"],
      mutationFn: async (body: { appkey: string; ledger_id: string; notes: string }) => {
        return api.patch(`/ledger/update/notes`, body);
      },
    });
  },
  useExecuteActum: () => {
    return useMutation({
      mutationKey: ["execute/actum"],
      mutationFn: async ({ appKey }: { appKey: string }) => {
        return api.post(`/application/payments/execute-actum/${appKey}`);
      },
    });
  },
  useArchiveApplication: () => {
    return useMutation({
      mutationKey: ["application/archive"],
      mutationFn: async ({ id }: { id: string }) => {
        return api.patch(`/application/archive/${id}`);
      },
    });
  },

  useSaveCustomStrategies: () => {
    return useMutation({
      mutationKey: ["application/strategy/custom", "save-strategy"],
      mutationFn: async ({ body, appkey }: { appkey: string; body: T_CustomStrategy[] }) => {
        return api.post(`/application/strategy/custom/${appkey}`, body);
      },
    });
  },
  useSaveCustomStrategiesV2: () => {
    return useMutation({
      mutationKey: ["application/strategy/custom-v2", "save-strategy-v2"],
      mutationFn: async ({ body, appkey }: { appkey: string; body: StrategyConfigV2[] }) => {
        return api.post(`/application/strategy/custom/v2/${appkey}`, body);
      },
    });
  },

  useGetCustomStrategies: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["get-strategy"],
      queryFn: async () => {
        const res = await api.get(`/application/strategy/custom/v2/${appkey}`);
        return res.data as T_Api_Success_Res<{
          v1: T_CustomStrategy[];
          v2: StrategyConfigV2[];
        }>;
      },
      enabled: !!appkey,
    });
  },

  useSendCustomStrategies: () => {
    return useMutation({
      mutationKey: ["application/strategy/custom/send"],
      mutationFn: async ({ appkey }: { appkey: string }) => {
        return api.post(`/application/strategy/custom/send/${appkey}`);
      },
    });
  },

  useGetIntelligenceNumbers: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["application/intelligence", appkey],
      queryFn: async () => {
        const res = await api.get(`/application/intelligence/${appkey}`);
        const data = res.data as T_Api_Success_Res<IntelligenceDto>;
        return data;
      },
      enabled: !!appkey,
    });
  },

  useProcessDebtorSummary: (appkey: string | undefined) => {
    return useMutation({
      mutationKey: ["application/statement-process"],
      mutationFn: async (body: { app_key: string; type: string }) => {
        return api.post(`/application/statement-process`, body);
      },
    });
  },

  useRejectManagerApproval: () => {
    return useMutation({
      mutationKey: ["application/strategy/custom/reject-approval"],
      mutationFn: async ({ appkey, cancel }: { appkey: string; cancel: boolean }) => {
        const url = cancel
          ? `/application/strategy/custom/reject-approval/${appkey}?cancel=true`
          : `/application/strategy/custom/reject-approval/${appkey}`;
        return api.post(url);
      },
    });
  },

  //   useStartCreditorIntel: () => {
  //   return useMutation({
  //     mutationKey: ["start/ai-intel"],
  //     mutationFn: async (appKey: string) => {
  //       return api.post(`/application/ai-intel/${appKey}`, {});
  //     },
  //   });
  // },

  useUpdatePaymentLedger: () => {
    return useMutation({
      mutationKey: ["update/ledger"],
      mutationFn: async (body: {
        appkey: string;
        idx: number; // ledger.week
        mode: "single" | "multiple";

        program_fee?: number;
        creditor?: {
          id: string; // creditor_id inside ledger.creditors
          amount: number;
        };
      }) => {
        const res = await api.patch(`/ledger/edit`, {
          ...body,
          creditor: body.creditor ? [body?.creditor] : undefined,
        });
        return res.data;
      },
    });
  },

  useGetCreditorIntel: () => {
    return useMutation({
      mutationKey: ["application/get-creditor-intel"],
      mutationFn: async (appKey: string) => {
        const resSettlement: AxiosResponse<
          T_Api_Success_Res<
            Record<
              string,
              {
                "Weekly Payment": number;
                "Final Weekly Settlement": number;
              }
            >[]
          >
        > = await api.post(`/application/ai-intel/${appKey}`);
        const resRisk: AxiosResponse<
          T_Api_Success_Res<
            {
              creditor_name: string;
              aggression_level: number;
              cond: string;
            }[]
          >
        > = await api.get(`/application/get-creditor-agression/${appKey}`);

        const creditorIntel = resRisk?.data?.result?.map((creditor) => {
          return {
            ...creditor,
            final_settlement: resSettlement?.data?.result?.find((loan) => creditor?.creditor_name in loan)?.[
              creditor?.creditor_name
            ]?.["Final Weekly Settlement"],
          };
        });
        return creditorIntel;
      },
    });
  },

  useGetUCCScore: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["application/get-ucc-score", appkey],
      queryFn: async () => {
        const res = await api.get(`/application/get-ucc-score/${appkey}`);
        return res.data as T_Api_Success_Res<{ ucc_score: number | undefined }>;
      },
      enabled: !!appkey,
    });
  },
  useGetAiIntelSummary: () => {
    return useMutation({
      mutationKey: ["application/intel/rebuild-summary"],
      mutationFn: async (appkey: string) => {
        const res = await api.get(`/application/intel/rebuild-summary/${appkey}`);
        return res.data as T_Api_Success_Res<{ format: "mardown" | "json"; value: string }>;
      },
    });
  },
  useChangePandaDocs: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/change-pandadoc"],
      mutationFn: async (body: Record<string, string>) => {
        const res = await api.post(`/application/change-pandadoc/${appkey}`, body);
        return res.data as T_Api_Success_Res<{
          upstream_response: Record<string, { status: "success" | "failed"; reason?: string; new_doc_id: string }>;
        }>;
      },
    });
  },
  useResetSteps: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/reset"],
      mutationFn: async (body: { modules: (typeof APPLICATION_STATUS)[keyof typeof APPLICATION_STATUS][] }) => {
        const res = await api.post(`/application/reset/${appkey}`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useChangeStatus: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/change-status"],
      mutationFn: async (body: { status: typeof APPLICATION_STATUS.SIGNATURES }) => {
        const res = await api.post(`/application/change-status/${appkey}`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },

  useValidateCalculator: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/validate/creditor-split/"],
      mutationFn: async (body: CreditorSplitFormat) => {
        const res = await api.post(`/application/validate/creditor-split/${appkey}`, body);
        return res.data as T_Api_Success_Res<{
          creditor: Record<string, { [Key in "warning" | "error"]: string }>;
          error_codes: string[];
          warning_codes: string[];
          weeks: Record<number, { [Key in "warning" | "error"]: string }>;
        }>;
      },
    });
  },
  useSaveCalculatorSave: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/ledger-save/"],
      mutationFn: async (body: CreditorSplitFormat) => {
        const res = await api.post(`/application/ledger-save/${appkey}`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useSaveCalculatorExecute: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/ledger-execute/"],
      mutationFn: async (body: CreditorSplitFormat) => {
        const res = await api.post(`/application/ledger-execute/${appkey}`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useGetCalculatorExecuteSuggestion: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["application/suggestion/creditor-split", appkey],
      queryFn: async () => {
        const res = await api.get(`/application/suggestion/creditor-split/${appkey}`);
        return res.data as T_Api_Success_Res<{
          suggestion: CreditorSplitFormat;
          creditorTotals: Record<string, number>;
        }>;
      },
      enabled: !!appkey,
    });
  },
  useGetCalculatorExecuteSaved: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["application/ledger-saved", appkey],
      queryFn: async () => {
        const res = await api.get(`/application/ledger-saved/${appkey}`);
        return res.data as T_Api_Success_Res<ApplicationSchemaDto["calculator_save"] | null>;
      },
      enabled: !!appkey,
    });
  },
  useCalculatorCreditorNegotiate: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/negotiate/process/"],
      mutationFn: async (body: { creditor_id: string; suggested_amount?: number }) => {
        const res = await api.post(`/application/negotiate/process/${appkey}`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useCalculatorCreditorManualAccept: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/negotiate/manual-accept/"],
      mutationFn: async (body: { creditor_id: string }) => {
        const res = await api.post(`/application/negotiate/manual-accept/${appkey}`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useCalculatorCreditorManualOverride: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/negotiate/manual-override/"],
      mutationFn: async (body: { creditor_id: string; override_amount: number }) => {
        const res = await api.post(`/application/negotiate/manual-override/${appkey}`, body);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },
  useCalculatorCreditorAutoCalculate: (appkey: string) => {
    return useMutation({
      mutationKey: ["application/negotiate/auto-calculate/"],
      mutationFn: async (body: { creditor_id: string; force?: boolean }) => {
        const res = await api.post(
          `/application/negotiate/auto-calculate/${appkey}`,
          { creditor_id: body.creditor_id },
          { params: { force: body?.force } },
        );
        return res.data as T_Api_Success_Res<{ code?: CalculatorValidationCodes; message: string; suggested?: number }>;
      },
    });
  },

  useGetFIAReport: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["application/get-fia", appkey],
      queryFn: async () => {
        const res = await api.get(`/report/${appkey}`);
        return res.data as T_Api_Success_Res<ReportDto>;
      },
      enabled: !!appkey,
    });
  },

  useGenerateFIAReport: () => {
    return useMutation({
      mutationKey: ["application/generate-fia"],
      mutationFn: async (appkey: string) => {
        const res = await api.post(`/report/generate/${appkey}`);
        return res.data as T_Api_Success_Res<any>;
      },
    });
  },

  useGetJourneyStatistics: () => {
    return useQuery({
      queryKey: ["application/journey-statistics"],
      queryFn: async () => {
        const res = await api.get(`/application/journey/statistics`);
        return res.data as T_Api_Success_Res<FlowData>;
      },
    });
  },

  useRetryMoneyThumb: () => {
    return useMutation({
      mutationKey: ["application/retry-moneythumb"],
      mutationFn: async (appkey: string) => {
        const res = await api.post(`/application/moneythumb/retry/${appkey}`);
      },
    });
  },

  useWriteNote: () => {
    return useMutation({
      mutationKey: ["application/add-notes"],
      mutationFn: async (body: { appkey: string; notes: string }) => {
        const res = await api.put(`/application/add-notes/${body.appkey}`, { notes: body.notes });
      },
    });
  },

  useGetBankDataML: ({
    appkey,
    start_date,
    end_date,
    page,
    limit,
    sortBy,
    sortOrder,
    category,
  }: {
    appkey?: string;
    start_date?: string;
    end_date?: string;
    page: number;
    limit: number;
    sortBy?: string;
    sortOrder?: number;
    category?: string[];
  }) => {
    return useQuery({
      queryKey: ["application/bank-cache", appkey, start_date, end_date, page, limit, sortBy, sortOrder, category],
      queryFn: async () => {
        const res = await api.get(
          `/application/ml/bank-cache/${appkey}?page=${page}&limit=${limit}${start_date?.length ? `&start_date=${start_date}` : ""}${end_date?.length ? `&end_date=${end_date}` : ""}${sortBy ? `&sortBy=${sortBy}` : ""}${sortOrder ? `&sortOrder=${sortOrder}` : ""} ${category && category.length > 0 ? category.map((cat) => `&category=${cat}`).join("") : ""}`,
        );
        return res.data as T_Api_Success_Res<{
          data: {
            id: number;
            appkey: string;
            account_number: string;
            transaction_date: string;
            credit_amount: number;
            debit_amount: number;
            description: string;
            creditordebit: string;
            category: string;
            subcategory: string | null;
            updated: string;
          }[];
          hasNextPage: boolean;
          hasPrevPage: boolean;
          nextPage: number | null;
          prevPage: number | null;
          limit: number;
          page: number;
          totalObjects: number;
          totalPages: number;
          timestamps: string;
        }>;
      },
      enabled: !!appkey,
    });
  },

  useUpdateBankCacheCategory: () => {
    return useMutation({
      mutationKey: ["/transactions/auto-categorize-manual"],
      mutationFn: async (body: { category: string; description: string; appkey: string; scope: string }) => {
        const res = await api.put(`/application/ml/bank-cache-update-scope`, {
          appkey: body.appkey,
          category: body.category,
          description: body.description,
          scope: body.scope,
        });

        return res.data;
      },
    });
  },

  useUpdateSingleBankCacheCategory: () => {
    return useMutation({
      mutationKey: ["application/bank-cache/update-category"],
      mutationFn: async (body: { id: number; category: string; subcategory?: string }) => {
        await api.put(`/application/ml/bank-cache-update/${body.id}`, {
          category: body.category,
          subcategory: body.subcategory,
        });
      },
    });
  },

  useUpdateSingleBankCacheCategoryByScope: () => {
    return useMutation({
      mutationKey: ["application/bank-cache/update-category"],
      mutationFn: async (body: { appkey: string; category: string; description: string; scope: string }) => {
        await api.put(`/application/ml/bank-cache-update-scope`, {
          category: body.category,
          description: body.description,
          appkey: body.appkey,
          scope: body.scope,
        });
      },
    });
  },

  useBulkUpdateBankCacheCategory: () => {
    return useMutation({
      mutationKey: ["application/bank-cache/update-category/bulk"],
      mutationFn: async (body: { id: number[]; category: string; subcategory?: string }) => {
        await api.put(`/application/ml/bank-cache-update-bulk`, {
          ids: body.id,
          category: body.category,
          subcategory: body.subcategory,
        });
      },
    });
  },

  // usePoolingCategoryUpdate: () => {
  //   return useMutation({
  //     mutationKey: ["transactionInfo"],
  //     mutationFn: async (transactionid: string) => {
  //       const res = await api_ML.get(`/result/${transactionid}`);
  //       console.log(res.data);

  //       return res.data.status as "pending" | "started" | "completed" | "failed";
  //     },
  //   });
  // },

  useMarkReceivable: () => {
    return useMutation({
      mutationKey: ["application/bank-cache/mark-receivable"],
      mutationFn: async (body: { appkey: string; category: string; value: boolean }) => {
        await api.post(`/application/ml/bank-cache/mark-receivable`, body);
      },
    });
  },

  useGetMarkedReceivables: (appkey?: string) => {
    return useQuery({
      queryKey: ["application/bank-cache/marked-receivables", appkey],
      queryFn: async () => {
        const res = await api.get(`/application/ml/bank-cache/get-receivables/${appkey}`);

        return res.data.result as {
          saved: Record<string, boolean>;
          upstream: string[];
        };
      },
      enabled: !!appkey,
    });
  },

  useGetTransactionMonths: (appkey?: string) => {
    return useQuery({
      queryKey: ["application/bank-cache/months", appkey],
      queryFn: async () => {
        const res = await api.get(`/application/ml/bank-cache/get-months/${appkey}`);

        return res.data.result as {
          months: string[];
        };
      },
      enabled: !!appkey,
    });
  },
  useUpdateProtectedReceivbles: () => {
    return useMutation({
      mutationKey: ["application/receivables-protected"],
      mutationFn: async ({ appkey, protected_recievables }: { appkey: string; protected_recievables: string[] }) => {
        const res = await api.patch(`application/protected-recievables/${appkey}`, { protected_recievables });
        return res.data;
      },
    });
  },

  useGetAdditionalEmails: (appKey: string | undefined) => {
    return useQuery({
      queryKey: ["application", "additional-emails", appKey],
      queryFn: async () => {
        const res = await api.get(`/application/emails?appKey=${appKey}`);
        return res.data as T_Api_Success_Res<{ extra: string[]; existing: string[] }>;
      },
      enabled: !!appKey,
    });
  },

  useGetPreviewAgreementPDF: (appKey: string | undefined) => {
    return useQuery({
      queryKey: ["application", "preview-agreement-pdf", appKey],
      queryFn: async () => {
        const res = await api.get(`/application/preview/agreement/${appKey}`, {
          responseType: "text",
        });
        return res.data;
      },
      enabled: false,
    });
  },

  useAddAdditionalEmails: () => {
    return useMutation({
      mutationFn: async ({ appKey, emails }: { appKey: string | undefined; emails: string[] }) => {
        const res = await api.patch<T_Api_Success_Res<{ emails: string[]; inserted: number; deleted: number }>>(
          `/application/emails?appKey=${appKey}`,
          { emails },
        );
        return res.data;
      },
    });
  },
  usePartialExecute: () => {
    return useMutation({
      mutationKey: ["application", "partial-execute"],
      mutationFn: async (body: {
        appkey: string;
        weekly_payment: number;
        creditor_id: string;
        start_week: number;
        overwriteExists?: boolean;
        overrideRowVal?: boolean;
      }) => {
        const { overwriteExists, overrideRowVal, ...restBody } = body;
        const res = await api.post(`/application/partial-execute`, restBody, {
          params: { overwriteExists, overrideRowVal },
        });
        return res.data as T_Api_Success_Res<{
          error?: {
            code:
              | "PL001" // overwriteExists flag requirement (case of involvement in the given start_week already)
              | "PL002"; // row extension (no flag)
            // | "PL003"; // overrideRowVal flag requirement (case of exceeding row creditor budget)
            message: string;
            extras?: { weeks: number };
          };
        }>;
      },
    });
  },
  useGetDSRiskReportOptions: (
    appkey: string | undefined,
    options?: Omit<
      UseQueryOptions<
        T_Api_Success_Res<{
          ruleSetIds: string[];
          aiAuditReportKeys: string[];
        }>,
        AxiosError<T_Api_Error_Res>
      >,
      "queryKey" | "queryFn"
    >,
  ) => {
    return useQuery({
      queryKey: ["application", "ds-risk/report/options", appkey],
      queryFn: async () => {
        const res = await api.get<
          T_Api_Success_Res<{
            ruleSetIds: string[];
            aiAuditReportKeys: string[];
          }>
        >(`/application/ds/risk/options/${appkey}`);

        return res.data;
      },
      enabled: !!appkey,
      ...options,
    });
  },
  useGetBackgroundLogs: (
    appkey: string | undefined,
    params: {
      page: number;
      limit: number;
      search?: string;
      job_type?: string;
      status?: string;
      start_date?: string;
      end_date?: string;
    },
  ) => {
    return useQuery({
      queryKey: ["background-job", appkey, params],
      queryFn: async () => {
        const res = await api.get(`/background-jobs/${appkey}`, {
          params,
        });

        return res.data as T_Api_Success_Res<T_Api_Paginated<BackgroundLog>>;
      },
      enabled: !!appkey,
    });
  },
  useGetDSRiskRunReport: () => {
    return useMutation({
      mutationKey: ["application", "ds-risk/report/run"],
      mutationFn: async (body: {
        name: string;
        email: string;
        ruleSetIds: string[];
        aiAuditReportKeys: string[];
        appkey: string;
      }) => {
        const res = await api.post<
          T_Api_Success_Res<{ redirectUrl: string; aprRedirectUrl: string; bankStatementRedirectUrl?: string }>
        >(`/application/ds/risk/run-report`, body);
        return res.data;
      },
    });
  },

  useTopupApplicationWallet: () => {
    return useMutation({
      mutationKey: ["application", "wallet/topup"],
      mutationFn: async (body: {
        application_id: string;
        budget: number;
        ref_transaction_id?: string;
        posting_date?: string | Date;
        force_create?: "true" | "false";
      }) => {
        const res = await api.post(`/application/wallet/topup`, body);
        return res.data as T_Api_Success_Res<object>;
      },
    });
  },

  useGetWalletBalance: (applicationId: string | undefined) => {
    return useQuery({
      queryKey: ["application", "wallet/balance", applicationId],
      queryFn: async () => {
        const res = await api.get(`/application/wallet/balance/${applicationId}`);
        return res.data as T_Api_Success_Res<{ balance: number }>;
      },
      enabled: !!applicationId,
    });
  },

  useGetPaymentMethodInfo: (appkey: string | undefined) => {
    return useQuery({
      queryKey: ["application", "payment/methods", appkey],
      queryFn: async () => {
        const res = await api.get(`/application/payment/info/${appkey}`);
        return res.data as T_Api_Success_Res<ApplicationSchemaDto["payment_info"]>;
      },
      enabled: !!appkey,
    });
  },

  useEditPaymentMethodInfo: () => {
    return useMutation({
      mutationKey: ["application", "edit/payment-info"],
      mutationFn: async (body: { appkey: string; ids: string[] }) => {
        const res = await api.patch(`/application/edit-payment-info/${body.appkey}`, { ids: body.ids });
        return res.data;
      },
    });
  },

  useSetStrategyPercentage: () => {
    return useMutation({
      mutationKey: ["percentage-strategy"],
      mutationFn: async ({ values, appKey }: { values: any; appKey?: string }) => {
        const res = await api.post(`/application/strategy/percentage?appKey=${appKey}`, { ...values });
        return res.data;
      },
    });
  },

  useBulkUpdateLedger: () => {
    return useMutation({
      mutationKey: ["prior-application", "prior/ledger-bulk-update"],
      mutationFn: async (body: {
        appKey: string;
        rows: Array<{
          _id?: string; //@Subhajit -> no need to send, API will delete all ledgers and recreate so new ids will be made
          week: number;
          amount: number;
          distribution: {
            program_fee: number;
            service_fee: number;
            legal_fee: number;
            creditor_total: number;
          };
          creditors: { creditor_id: string; amount: number }[];
          posting_date?: Date | string;
          payment_date?: Date | string;
          status: string;
          isNew: boolean; //@Subhajit -> not needed as API will delete and add all fresh
        }>;
      }) => {
        const res = await api.post(`/application/prior/ledger-bulk-update/${body.appKey}`, body.rows);
        return res.data as T_Api_Success_Res<object>;
      },
    });
  },

  useGetPaynoteFundingSourcesViaEmail: (email: string | undefined) => {
    return useQuery({
      queryKey: ["application/payment-methods", "paynote-source", email],
      queryFn: async () => {
        const res = await api.get(`/application/paynote/funding-sources/${encodeURIComponent(email!)}`);
        return res.data as T_Api_Success_Res<
          {
            type?: "savings" | "checking";
            status: string;
            bank: string;
            source_id: string;
            lbacc: string;
          }[]
        >;
      },
      enabled: !!email,
    });
  },

  useImportPaynote: () => {
    return useMutation({
      mutationKey: ["application/payment-methods", "import-paynote"],
      mutationFn: async ({
        ref_id,
        source_ids,
        email,
        type,
      }: {
        source_ids: string[];
        email: string;
        ref_id: string;
        type: "Application" | "Creditor";
      }) => {
        const res = await api.post(`/application/paynote/import`, { ref_id, source_ids, email, type });
        return res.data as T_Api_Success_Res<object>;
      },
    });
  },

  useGetTeamMembers: (appKey: string | undefined) => {
    return useQuery({
      queryKey: ["application", appKey, "members"],
      queryFn: async () => {
        const { data } = await api.get<
          T_Api_Success_Res<
            { user: Pick<UserSchemaDto, "_id" | "name" | "email" | "role" | "phone">; role: TeamRole }[]
          >
        >(`/application/members/${appKey}`);
        return data.result;
      },
      enabled: Boolean(appKey?.length),
    });
  },
  useUpdateTeamMembers: () => {
    return useMutation({
      mutationFn: async ({ appKey, members }: { appKey: string; members: TeamMember[] }) => {
        const { data } = await api.patch<T_Api_Success_Res<TeamMember[]>>(`/application/members/${appKey}`, {
          members,
        });
        return data.result;
      },
    });
  },
  useUpdateMeetings: () => {
    return useMutation({
      mutationFn: async ({ appKey, meetings }: { appKey: string; meetings: Meeting[] }) => {
        const { data } = await api.patch<T_Api_Success_Res<Meeting[]>>(`/application/meetings/${appKey}`, {
          meetings,
        });

        return data.result;
      },
    });
  },
  useUpdateStage: () => {
    return useMutation({
      mutationFn: async ({ appKey, stage }: { appKey: string; stage: string }) => {
        const { data } = await api.patch<T_Api_Success_Res<unknown>>(`/application/stage/${appKey}`, { id: stage });
        return data.result;
      },
    });
  },
  usePendingTransactions: ({ appKey, loanId, enabled = true }: T_LoanScopeParams & { enabled?: boolean }) => {
    return useQuery({
      queryKey: ["loan-transactions", "pending", appKey, loanId],

      queryFn: async () => {
        const res = await api.get(`/application/loan-payment/${appKey}/loans/${loanId}/transactions/pending`);
        return res.data as T_Api_Success_Res<{ status: "processing" | "ok"; transactions: T_MergedTransaction[] }>;
      },

      enabled: enabled && !!appKey && !!loanId,
      refetchInterval: (query) => (query.state.data?.result.status === "processing" ? 5000 : false),
    });
  },

  useAcceptedTransactions: ({ appKey, loanId, enabled = true }: T_LoanScopeParams & { enabled?: boolean }) => {
    return useQuery({
      queryKey: ["loan-transactions-accepted", "accepted", appKey, loanId],

      queryFn: async () => {
        const res = await api.get(`/application/loan-payment/${appKey}/loans/${loanId}/transactions/accepted`);
        return res.data as T_Api_Success_Res<{ status: "processing" | "ok"; transactions: T_MergedTransaction[] }>;
      },

      enabled: enabled && !!appKey && !!loanId,
      refetchInterval: (query) => (query.state.data?.result.status === "processing" ? 5000 : false),
    });
  },

  useAcceptTransaction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["loan-transactions-update"],

      mutationFn: async ({ appKey, loanId, transactionId }: T_LoanScopeParams & { transactionId: string }) => {
        const res = await api.patch(
          `/application/loan-payment/${appKey}/loans/${loanId}/transactions/${transactionId}/accept`,
        );
        return res.data as T_Api_Success_Res<unknown>;
      },

      onSuccess: (_data, variables) => {
        // The transaction moves from pending -> accepted, so both lists change.
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions", "pending", variables.appKey, variables.loanId],
        });
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions-accepted", "accepted", variables.appKey, variables.loanId],
        });
      },
    });
  },

  useRejectTransaction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["loan-transactions", "reject"],

      mutationFn: async ({ appKey, loanId, transactionId }: T_LoanScopeParams & { transactionId: string }) => {
        const res = await api.patch(
          `/application/loan-payment/${appKey}/loans/${loanId}/transactions/${transactionId}/reject`,
        );
        return res.data as T_Api_Success_Res<unknown>;
      },

      onSuccess: (_data, variables) => {
        // Rejected items only ever leave the pending list — accepted is untouched.
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions", "pending", variables.appKey, variables.loanId],
        });
      },
    });
  },

  useAddManualEntry: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["loan-transactions", "manual", "add"],

      mutationFn: async ({ appKey, loanId, body }: T_LoanScopeParams & { body: T_CreateManualEntryRequest }) => {
        const res = await api.post(`/application/loan-payment/${appKey}/loans/${loanId}/transactions/manual`, body);
        return res.data as T_Api_Success_Res<T_MergedTransaction>;
      },

      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions-accepted", "accepted", variables.appKey, variables.loanId],
        });
      },
    });
  },

  useUpdateManualEntry: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["loan-transactions", "manual", "update"],

      mutationFn: async ({
        appKey,
        loanId,
        entryId,
        body,
      }: T_LoanScopeParams & { entryId: string; body: T_UpdateManualEntryRequest }) => {
        const res = await api.patch(
          `/application/loan-payment/${appKey}/loans/${loanId}/transactions/manual/${entryId}`,
          body,
        );
        return res.data as T_Api_Success_Res<T_MergedTransaction>;
      },

      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions-accepted", "accepted", variables.appKey, variables.loanId],
        });
      },
    });
  },

  useDeleteManualEntry: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["loan-transactions", "manual", "delete"],

      mutationFn: async ({ appKey, loanId, entryId }: T_LoanScopeParams & { entryId: string }) => {
        const res = await api.delete(
          `/application/loan-payment/${appKey}/loans/${loanId}/transactions/manual/${entryId}`,
        );
        return res.data as T_Api_Success_Res<{ deleted: boolean }>;
      },

      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions-accepted", "accepted", variables.appKey, variables.loanId],
        });
      },
    });
  },
  useBulkUpdateTransactions: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["applications", "loans", "transactions", "bulk"],

      mutationFn: async ({ appKey, loanId, body }: T_BulkUpdateTransactionsParams) => {
        const res = await api.patch(`/application/loan-payment/${appKey}/loans/${loanId}/transactions/bulk`, body);

        return res.data;
      },

      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions", "pending", variables.appKey, variables.loanId],
        });
        queryClient.invalidateQueries({
          queryKey: ["loan-transactions-accepted", "accepted", variables.appKey, variables.loanId],
        });
      },
    });
  },
};
